import { get } from '../Shared/ajax';
import { useEffect, useReducer, useState } from 'react';
import { useSnackbar } from 'notistack';
import PageState from '../Shared/pageState';
import {
  EventData,
  initialState,
  VersionData,
  WorkflowEngineActionKind,
  WorkflowEngineContext,
  WorkflowEngineMode,
  workflowEngineReducer,
  WorkflowEngineState
} from './workflowEngineReducer';
import { getErrorMessage } from '../Shared/errorHandling';
import workflowEngineDictionary from './workflowEngineDictionary';
import { JSONSchema7 } from 'json-schema';
import upgradePageSchema from './SchemaUpgrade/upgradePageSchema';
import saveRetrievedPageUpgrades from './SchemaUpgrade/saveRetrievedPageUpgrades';
import {
  WorkflowEngineProps,
  GetWorkflowResponse,
  WorkflowLockStatus
} from './types';
import config from '../Shared/config';
import NoAccessView from '../FormEngine/Entry/NoAccessView';
import hasRoleAccessToPage from '../Shared/jsonSchemaUtilities/hasRoleAccessToPage';
import getControlsFromPageSchema from '../Shared/jsonSchemaUtilities/getControlsFromPageSchema';

export default (props: WorkflowEngineProps) => {
  const firstLoadState: WorkflowEngineState = {
    ...initialState,
    mode: props.mode,
    role: props.role
  };

  const [state, dispatch] = useReducer(workflowEngineReducer, firstLoadState);

  const {
    mode,
    role,
    workflowType,
    workflowEntryExternalId,
    versionData,
    versionExternalId
  } = state;

  const [loading, setLoading] = useState<boolean>(true);
  const [hasPagePermission, setHasPagePermission] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const indexOfPageToLoad = (pages: PageState[]): number => {
    if (mode !== WorkflowEngineMode.ENTRY) return 0;

    const firstPageAvailableToRole = pages.find((page) =>
      hasRoleAccessToPage(mode, role, page)
    );

    return firstPageAvailableToRole
      ? pages.indexOf(firstPageAvailableToRole)
      : -1;
  };

  const handleFetchJsonSchemaSucceded = (
    response: GetWorkflowResponse,
    entryExternalId?: string
  ) => {
    let isSchemaUpgraded = response.isSchemaUpgraded;

    const retrievedPages: PageState[] = response.pages.map<PageState>((p) => {
      const pageSchema = JSON.parse(p.metadata) as JSONSchema7;
      const latestSchema: JSONSchema7 = upgradePageSchema(pageSchema);
      const controls = getControlsFromPageSchema(p.externalId, latestSchema);

      if (!isSchemaUpgraded) {
        isSchemaUpgraded = p.metadata !== JSON.stringify(latestSchema);
      }

      return {
        id: p.externalId,
        schema: latestSchema,
        controls
      };
    });

    const versionData: VersionData | undefined =
      response.versionData || undefined;

    // Determine the index of the first page to which this user has permission
    const pageIndexToLoad = indexOfPageToLoad(retrievedPages);

    // If there is no page index >= 0, this user does not have permission to view any of the pages
    if (pageIndexToLoad === -1) return;

    setHasPagePermission(true);

    const payload: WorkflowEngineState = {
      ...state,
      externalId: response.externalId,
      currentPage: pageIndexToLoad,
      isPaused: response.isPaused,
      isReadOnlyView: versionData?.isPublished,
      lastUpdatedBy: response.lastUpdatedBy,
      lastUpdatedDate: response.lastUpdatedDate,
      pages: retrievedPages,
      publishedVersionExternalId:
        response.publishedVersionExternalId || undefined,
      versionData: versionData,
      versionExternalId: response.versionExternalId,
      workflowEntryExternalId: entryExternalId,
      workflowName: response.name,
      workflowType: response.workflowType,
      lockStatus: response.lockStatus
    };

    dispatch({
      type: WorkflowEngineActionKind.FIRST_LOAD,
      payload
    });

    if (isSchemaUpgraded) {
      // eslint-disable-next-line no-console
      console.info('Workflow upgraded:', retrievedPages);
      saveRetrievedPageUpgrades(retrievedPages);
    }
  };

  const fetchJsonSchemaForVersion = async (versionExternalId: string) => {
    setLoading(true);

    try {
      const url = `DynamicWorkflowVersion/${versionExternalId}`;
      const response = await get<GetWorkflowResponse>(url);

      if (response) {
        handleFetchJsonSchemaSucceded(response);
      } else {
        enqueueSnackbar('No unpublished workflow found.', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }

    setLoading(false);
  };

  const fetchJsonSchemaForEntryId = async (entryId: string) => {
    setLoading(true);

    try {
      const url = `DynamicWorkflowEntry/Version?Id=${entryId}`;
      const response = await get<GetWorkflowResponse>(url);

      if (response) {
        handleFetchJsonSchemaSucceded(response, entryId);
      } else {
        enqueueSnackbar('No unpublished workflow found.', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }

    setLoading(false);
  };

  const switchToEditMode = (versionExternalId: string) => {
    dispatch({
      type: WorkflowEngineActionKind.SWITCH_TO_EDIT_MODE,
      payload: versionExternalId
    });
  };

  const fetchLatestVersionExternalId = async (workflowExternalId: string) => {
    setLoading(true);

    try {
      const url = `DynamicWorkflow/${workflowExternalId}/LatestVersion`;
      const response = await get<string>(url);
      if (response) {
        setHasPagePermission(true);
        switchToEditMode(response);
      } else {
        enqueueSnackbar('No unpublished workflow found.', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }

    setLoading(false);
  };

  const retrieveWorkflowLockStatusAsync = async (
    workflowExternalId: string
  ) => {
    try {
      const url = `DynamicWorkflow/${workflowExternalId}/LockStatus`;
      const response = await get<WorkflowLockStatus>(url);

      dispatch({
        type: WorkflowEngineActionKind.SET_WORKFLOW_LOCK_STATUS,
        payload: response
      });

      // eslint-disable-next-line no-empty
    } catch (error) {
      enqueueSnackbar((error as Error).message, { variant: 'error' });
    }
  };

  const retrieveEventData = async (
    entryExternalId: string,
    workflowType: string
  ) => {
    try {
      const apiUrl = `DynamicWorkflowService/EventData?dynamicWorkflowEntryExternalId=${entryExternalId}&workflowType=${workflowType}&userId=${config.userId}`;
      const response = await get<EventData>(apiUrl);

      if (!response) {
        enqueueSnackbar('No event data found.', { variant: 'error' });
        return;
      }

      dispatch({
        type: WorkflowEngineActionKind.SET_EVENT_DATA,
        payload: response
      });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
      return;
    }
  };

  useEffect(() => {
    if (workflowEntryExternalId) {
      retrieveEventData(workflowEntryExternalId, workflowType);
    }
  }, [workflowEntryExternalId]);

  useEffect(() => {
    let lockStatusTimer: NodeJS.Timeout | undefined;

    if (mode === WorkflowEngineMode.ENTRY) {
      fetchJsonSchemaForEntryId(props.externalId);
    } else {
      fetchLatestVersionExternalId(props.externalId);
      lockStatusTimer = setInterval(async () => {
        await retrieveWorkflowLockStatusAsync(props.externalId);
      }, 15000);
    }

    return () => {
      if (lockStatusTimer) clearInterval(lockStatusTimer as NodeJS.Timeout);
    };
  }, []);

  useEffect(() => {
    if (
      mode === WorkflowEngineMode.BUILD ||
      mode === WorkflowEngineMode.MAPPER
    ) {
      dispatch({
        type: WorkflowEngineActionKind.SET_READ_ONLY_VIEW,
        payload: versionData?.isPublished
      });
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== WorkflowEngineMode.ENTRY && versionExternalId) {
      fetchJsonSchemaForVersion(versionExternalId);
    }
  }, [versionExternalId]);

  return loading ? (
    workflowEngineDictionary[mode].loadingView
  ) : hasPagePermission ? (
    <WorkflowEngineContext.Provider value={{ state, dispatch }}>
      {workflowEngineDictionary[mode].container}
    </WorkflowEngineContext.Provider>
  ) : (
    <NoAccessView />
  );
};
